import { observable, action, makeObservable, computed, runInAction } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GET_KREDI_FLOW_SETTINGS, UPSERT_CUSTOMERSETTING } from "src/api/customer";
import { UpsertCustomersetting, UpsertCustomersettingVariables } from "src/api/generated/UpsertCustomersetting";
import { GetKrediflowSettings } from "src/api/generated/GetKrediflowSettings";
import i18next from "i18next";
import { MessageType } from "src/components/notifications/Notifier";
import { IUserToAssign } from "src/stores/krediflow/AssignInvoiceStore";

export type FallbackWorkflowUserRole = "visa1" | "visa2" | "visa3" | "accountant";

export interface IFallbackWorkflowUser {
    visa1: string | null;
    visa2: string | null;
    visa3: string | null;
    accountant: string | null;
}

export interface IFallbackWorkflowUserDisplay {
    visa1: string;
    visa2: string;
    visa3: string;
    accountant: string;
}

const DEFAULT_FALLBACK_WORKFLOW_USER: IFallbackWorkflowUser = {
    visa1: null,
    visa2: null,
    visa3: null,
    accountant: null
};

const DEFAULT_FALLBACK_WORKFLOW_USER_DISPLAY: IFallbackWorkflowUserDisplay = {
    visa1: "",
    visa2: "",
    visa3: "",
    accountant: ""
};

export class KrediFlowConfigurationStore {
    rootStore: RootStore;

    isLoading: boolean = false;
    manualVisaworkflowDefinition: boolean = false;
    originalManualVisaworkflowDefinition: boolean = false;

    numberOfVisa: number = 1;
    originalNumberOfVisa: number = 1;

    fallbackWorkflowUser: IFallbackWorkflowUser = { ...DEFAULT_FALLBACK_WORKFLOW_USER };
    originalFallbackWorkflowUser: IFallbackWorkflowUser = { ...DEFAULT_FALLBACK_WORKFLOW_USER };
    fallbackWorkflowUserDisplay: IFallbackWorkflowUserDisplay = { ...DEFAULT_FALLBACK_WORKFLOW_USER_DISPLAY };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            isLoading: observable,
            manualVisaworkflowDefinition: observable,
            originalManualVisaworkflowDefinition: observable,
            numberOfVisa: observable,
            originalNumberOfVisa: observable,
            fallbackWorkflowUser: observable,
            originalFallbackWorkflowUser: observable,
            fallbackWorkflowUserDisplay: observable,

            init: action,
            loadSettings: action,
            saveSettings: action,
            resetSettings: action,
            toggleManualVisaworkflowDefinition: action,
            setNumberOfVisa: action,
            setFallbackWorkflowUser: action,
            clearFallbackWorkflowUser: action,

            maxNumberOfVisa: computed,
            hasNumberOfVisaChanged: computed,
            hasFallbackWorkflowUserChanged: computed,
            hasChanged: computed
        });
    }

    init = async () => {
        await this.rootStore.assignInvoiceStore.loadUsersToAssign();
        await this.loadSettings();
    };

    toggleManualVisaworkflowDefinition = () => {
        this.manualVisaworkflowDefinition = !this.manualVisaworkflowDefinition;
    };

    setNumberOfVisa = (numberOfVisa: number) => {
        this.numberOfVisa = Math.min(Math.max(numberOfVisa, 1), this.maxNumberOfVisa);
    };

    // KrediFlow Pro customers can configure up to 3 visa steps, Basic customers up to 2.
    get maxNumberOfVisa(): number {
        return this.rootStore.navStore.hasFeatureKrediFlowPro ? 3 : 2;
    }

    setFallbackWorkflowUser = (field: FallbackWorkflowUserRole, user: IUserToAssign) => {
        this.fallbackWorkflowUser[field] = user.userid;
        this.fallbackWorkflowUserDisplay[field] = user.name;
    };

    clearFallbackWorkflowUser = (field: FallbackWorkflowUserRole) => {
        this.fallbackWorkflowUser[field] = null;
        this.fallbackWorkflowUserDisplay[field] = "";
    };

    resolveUserName = (userid: string | null): string => {
        if (!userid) return "";
        const users = this.rootStore.assignInvoiceStore.usersToAssignData;
        const user = users.find((u) => u.userid === userid);
        return user?.name ?? "";
    };

    loadSettings = async () => {
        this.isLoading = true;

        try {
            const result = await apolloClientInstance.query<GetKrediflowSettings>({
                query: GET_KREDI_FLOW_SETTINGS,
                fetchPolicy: "network-only"
            });

            const manualVisa = result.data.visaworkflow?.[0]?.value === "true";

            const numberOfVisa = Number(result.data.numberOfVisa?.[0]?.value) || 1;

            let fallbackUser: IFallbackWorkflowUser = { ...DEFAULT_FALLBACK_WORKFLOW_USER };
            const fallbackRaw = result.data.fallbackWorkflowUser?.[0]?.value;
            if (fallbackRaw) {
                try {
                    const parsed = JSON.parse(fallbackRaw);
                    const roles = parsed.workflow_user_roles;
                    if (roles) {
                        fallbackUser = {
                            visa1: roles.visa1 || null,
                            visa2: roles.visa2 || null,
                            visa3: roles.visa3 || null,
                            accountant: roles.accountant || null
                        };
                    }
                } catch (error) {
                    console.error("Failed to parse fallback workflow user JSON", error);
                }
            }

            runInAction(() => {
                this.manualVisaworkflowDefinition = manualVisa;
                this.originalManualVisaworkflowDefinition = manualVisa;
                this.numberOfVisa = numberOfVisa;
                this.originalNumberOfVisa = numberOfVisa;
                this.fallbackWorkflowUser = { ...fallbackUser };
                this.originalFallbackWorkflowUser = { ...fallbackUser };
                this.fallbackWorkflowUserDisplay = {
                    visa1: this.resolveUserName(fallbackUser.visa1),
                    visa2: this.resolveUserName(fallbackUser.visa2),
                    visa3: this.resolveUserName(fallbackUser.visa3),
                    accountant: this.resolveUserName(fallbackUser.accountant)
                };
            });
        } catch (error) {
            console.error("Failed to load KrediFlow configuration settings", error);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    saveSettings = async () => {
        this.isLoading = true;

        try {
            const upsertObjects: { key: string; value: string; valuetype: number }[] = [];

            if (this.manualVisaworkflowDefinition !== this.originalManualVisaworkflowDefinition) {
                upsertObjects.push({
                    key: "KREDIFLOW.MANUAL_VISAWORKFLOW_DEFINITION",
                    value: this.manualVisaworkflowDefinition.toString(),
                    valuetype: 1
                });
            }

            if (this.hasNumberOfVisaChanged) {
                upsertObjects.push({
                    key: "CRED.NUMBEROFVISA",
                    value: this.numberOfVisa.toString(),
                    valuetype: 2
                });
            }

            if (this.hasFallbackWorkflowUserChanged) {
                const fallbackJson = JSON.stringify({
                    workflow_user_roles: {
                        visa1: this.fallbackWorkflowUser.visa1 || null,
                        visa2: this.fallbackWorkflowUser.visa2 || null,
                        visa3: this.fallbackWorkflowUser.visa3 || null,
                        accountant: this.fallbackWorkflowUser.accountant || null
                    }
                });
                upsertObjects.push({
                    key: "CRED.FALLBACKWORKFLOWUSER",
                    value: fallbackJson,
                    valuetype: 3
                });
            }

            if (upsertObjects.length > 0) {
                await apolloClientInstance.mutate<UpsertCustomersetting, UpsertCustomersettingVariables>({
                    mutation: UPSERT_CUSTOMERSETTING,
                    variables: { objects: upsertObjects }
                });
            }

            runInAction(() => {
                this.originalManualVisaworkflowDefinition = this.manualVisaworkflowDefinition;
                this.originalNumberOfVisa = this.numberOfVisa;
                this.originalFallbackWorkflowUser = { ...this.fallbackWorkflowUser };

                // Keep the in-memory customer in sync so the workflow engine (which reads
                // navStore.customer.settings.cred.numberOfVisas directly) uses the new value
                // without requiring a page reload.
                const customer = this.rootStore.navStore.customer;
                customer.settings = {
                    ...customer.settings,
                    cred: { ...customer.settings?.cred, numberOfVisas: this.numberOfVisa }
                };
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.communication_settings.success_save_message"),
                MessageType.SUCCESS
            );
        } catch (error) {
            console.error("Failed to save KrediFlow configuration settings", error);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.communication_settings.failure_save_message"),
                MessageType.ERROR
            );
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    resetSettings = () => {
        this.manualVisaworkflowDefinition = this.originalManualVisaworkflowDefinition;
        this.numberOfVisa = this.originalNumberOfVisa;
        this.fallbackWorkflowUser = { ...this.originalFallbackWorkflowUser };
        this.fallbackWorkflowUserDisplay = {
            visa1: this.resolveUserName(this.originalFallbackWorkflowUser.visa1),
            visa2: this.resolveUserName(this.originalFallbackWorkflowUser.visa2),
            visa3: this.resolveUserName(this.originalFallbackWorkflowUser.visa3),
            accountant: this.resolveUserName(this.originalFallbackWorkflowUser.accountant)
        };
    };

    get hasNumberOfVisaChanged() {
        return this.numberOfVisa !== this.originalNumberOfVisa;
    }

    get hasFallbackWorkflowUserChanged() {
        return (
            this.fallbackWorkflowUser.visa1 !== this.originalFallbackWorkflowUser.visa1 ||
            this.fallbackWorkflowUser.visa2 !== this.originalFallbackWorkflowUser.visa2 ||
            this.fallbackWorkflowUser.visa3 !== this.originalFallbackWorkflowUser.visa3 ||
            this.fallbackWorkflowUser.accountant !== this.originalFallbackWorkflowUser.accountant
        );
    }

    get hasChanged() {
        return (
            this.manualVisaworkflowDefinition !== this.originalManualVisaworkflowDefinition ||
            this.hasNumberOfVisaChanged ||
            this.hasFallbackWorkflowUserChanged
        );
    }
}
